import "react-day-picker/lib/style.css";

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import DayPicker from "react-day-picker";
import styled from "styled-components";

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input`
  width: 100%;
  border: none;
  border-bottom: 1px solid #d4d4d4;
  background: transparent;
  font-size: 13px;
  color: #1c1917;
  padding: 4px 0 5px 0;
  outline: none;
  font-family: inherit;
  cursor: text;
  &:focus {
    border-bottom-color: #2185d0;
  }
  &::placeholder {
    color: #c4b5b0;
  }
`;

const CalendarPopup = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const VALID_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const InlineDateInput = ({
  value: propValue,
  onChange,
  placeholder,
}) => {
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    setText(propValue || "");
  }, [propValue]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const clamp = (raw) => {
    let result = raw;
    // Clamp month to 01-12 once MM is fully typed
    if (result.length >= 7 && result[4] === "-") {
      const mm = parseInt(result.slice(5, 7), 10);
      if (mm > 12) result = result.slice(0, 5) + "12" + result.slice(7);
      else if (mm < 1) result = result.slice(0, 5) + "01" + result.slice(7);
    }
    // Clamp day to 01-max days in month once DD is fully typed
    if (result.length >= 10 && result[4] === "-" && result[7] === "-") {
      const dd = parseInt(result.slice(8, 10), 10);
      const maxDay = dayjs(result.slice(0, 7) + "-01").daysInMonth();
      if (dd > maxDay)
        result = result.slice(0, 8) + String(maxDay).padStart(2, "0");
      else if (dd < 1) result = result.slice(0, 8) + "01";
    }
    return result;
  };

  const handleChange = (e) => {
    const raw = clamp(e.target.value);
    setText(raw);
    if (VALID_DATE.test(raw) && dayjs(raw).isValid()) {
      onChange(raw);
    } else if (raw === "") {
      onChange("");
    }
  };

  const handleBlur = (e) => {
    // Don't close if focus moves into the calendar popup
    if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
    if (!VALID_DATE.test(text) || !dayjs(text).isValid()) {
      setText(propValue || "");
    }
    setIsOpen(false);
  };

  const onDayClick = (date) => {
    const newValue = dayjs(date).format("YYYY-MM-DD");
    setText(newValue);
    setIsOpen(false);
    onChange(newValue);
  };

  const parsedDate =
    VALID_DATE.test(text) && dayjs(text).isValid()
      ? dayjs(text).toDate()
      : null;

  const calendarMonth = (() => {
    if (parsedDate) return parsedDate;
    const partial = dayjs(
      text.length >= 7 ? text.slice(0, 7) + "-01" : text + "-01-01"
    );
    return partial.isValid() && text.length >= 4
      ? partial.toDate()
      : new Date();
  })();

  return (
    <Wrap ref={wrapRef}>
      <DateInput
        value={text}
        placeholder={placeholder || "гггг-мм-дд"}
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Tab") setIsOpen(false);
        }}
      />
      {isOpen && (
        <CalendarPopup>
          <DayPicker
            onDayClick={onDayClick}
            month={calendarMonth}
            selectedDays={parsedDate}
          />
        </CalendarPopup>
      )}
    </Wrap>
  );
};

InlineDateInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
